import { motion, AnimatePresence } from "framer-motion";
import { Clock, Moon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "./Switch";
import { itemVariants } from "./motion";
import type { BusinessHoursSettings } from "../mockAutomations";

interface BusinessHoursCardProps {
  settings: BusinessHoursSettings;
  onChange: (patch: Partial<BusinessHoursSettings>) => void;
}

export function BusinessHoursCard({ settings, onChange }: BusinessHoursCardProps) {
  return (
    <motion.div variants={itemVariants} className="h-full">
      <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col">
        {/* Pod Header */}
        <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-4 py-2.5 gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
              <Clock className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Business Hours & Away Mode
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200/80 dark:border-indigo-900/60 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.2 text-[9px] font-bold text-indigo-700 dark:text-indigo-400">
              {settings.enabled ? "Active Schedule" : "24/7 Live"}
            </span>
          </div>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Offline behavior</span>
        </div>

        <div className="p-3.5 bg-white/70 dark:bg-slate-900/70 space-y-3 flex-1">
          <div className="flex items-center justify-between rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                <Moon className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-900 dark:text-slate-100">Use Business Hours</div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">{settings.hoursLabel}</p>
              </div>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => onChange({ enabled: checked })}
              aria-label="Toggle business hours"
            />
          </div>

          <AnimatePresence initial={false}>
            {settings.enabled && (
              <motion.div
                key="away-message"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-1.5 pt-1">
                  <Label htmlFor="away-message" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Away Auto-Reply Message</Label>
                  <textarea
                    id="away-message"
                    rows={3}
                    value={settings.awayMessage}
                    onChange={(e) => onChange({ awayMessage: e.target.value })}
                    placeholder="What should the AI say outside business hours?"
                    className="w-full resize-y rounded-xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-sans text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 shadow-2xs leading-relaxed"
                  />
                  <p className="text-[10px] text-slate-400 dark:text-slate-500">Sent automatically to incoming inquiries received outside operating hours.</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>
    </motion.div>
  );
}
